import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Shield, Lock, Eye, FileText, UserCheck, Server, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PoliticaPrivacidade = () => {
  const [logoBase64, setLogoBase64] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(res => {
      setLogoBase64(res.data?.logo_base64 || '');
    }).catch(() => {});
  }, []);

  const sections = [
    {
      icon: FileText,
      title: '1. Informacoes Gerais',
      content: `A Distribuidora Santa Ana, pessoa juridica de direito privado, com sede no Brasil, e a controladora responsavel pelo tratamento dos dados pessoais coletados por meio deste site. Nos levamos a protecao dos seus dados pessoais a serio e nos comprometemos a garantir a privacidade e a seguranca das informacoes fornecidas. Esta Politica de Privacidade descreve como coletamos, usamos, armazenamos e protegemos seus dados pessoais ao utilizar nosso site e servicos.`
    },
    {
      icon: Eye,
      title: '2. Dados Coletados',
      content: `Ao utilizar nosso site, podemos coletar os seguintes tipos de dados:\n\n- Dados de navegacao: informacoes sobre como voce utiliza nosso site, incluindo paginas visitadas, produtos visualizados, tempo de permanencia e dispositivo utilizado.\n- Dados de contato: quando voce entra em contato conosco via WhatsApp ou formulario, podemos coletar seu nome, numero de telefone e mensagens enviadas.\n- Dados de analytics: coletamos dados anonimos para fins estatisticos, como contagem de visitantes, cliques em produtos e visualizacoes de paginas.`
    },
    {
      icon: UserCheck,
      title: '3. Finalidade do Uso dos Dados',
      content: `Os dados coletados sao utilizados para as seguintes finalidades:\n\n- Melhorar a experiencia de navegacao e personalizar o conteudo apresentado.\n- Analisar o desempenho do site e identificar oportunidades de melhoria.\n- Responder a solicitacoes e fornecer atendimento ao cliente.\n- Enviar informacoes sobre produtos, promocoes e novidades (somente com seu consentimento).\n- Cumprir obrigacoes legais e regulatorias.`
    },
    {
      icon: Lock,
      title: '4. Seguranca dos Dados',
      content: `Adotamos medidas tecnicas e organizacionais adequadas para proteger seus dados pessoais contra acesso nao autorizado, perda, destruicao ou alteracao. Utilizamos protocolos de seguranca, incluindo criptografia e controle de acesso, para garantir a integridade e confidencialidade das informacoes armazenadas.`
    },
    {
      icon: Server,
      title: '5. Armazenamento e Retencao',
      content: `Seus dados pessoais serao armazenados pelo periodo necessario para cumprir as finalidades descritas nesta politica, ou conforme exigido por lei. Dados de analytics sao armazenados de forma anonimizada e podem ser retidos por periodo indeterminado para fins estatisticos.`
    },
    {
      icon: Shield,
      title: '6. Seus Direitos',
      content: `De acordo com a Lei Geral de Protecao de Dados (LGPD), voce possui os seguintes direitos:\n\n- Confirmacao da existencia de tratamento de dados.\n- Acesso aos seus dados pessoais.\n- Correcao de dados incompletos, inexatos ou desatualizados.\n- Anonimizacao, bloqueio ou eliminacao de dados desnecessarios.\n- Portabilidade dos dados a outro fornecedor.\n- Eliminacao dos dados tratados com consentimento.\n- Revogacao do consentimento a qualquer momento.`
    },
    {
      icon: Mail,
      title: '7. Contato',
      content: `Para exercer seus direitos ou esclarecer duvidas sobre esta Politica de Privacidade, entre em contato conosco pelos canais de atendimento disponiveis no site ou pelo WhatsApp.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="privacy-logo-link">
            {logoBase64 ? (
              <img src={logoBase64} alt="Santa Ana" className="h-10 w-auto" />
            ) : null}
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">Santa Ana</h1>
              <p className="text-xs text-orange-600 font-medium">Distribuidora</p>
            </div>
          </a>
          <a
            href="/"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors"
            data-testid="back-to-catalog-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Catalogo
          </a>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Politica de Privacidade
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Sua privacidade e importante para nos. Saiba como tratamos seus dados pessoais.
          </p>
          <p className="text-blue-300 text-sm mt-4">
            Ultima atualizacao: {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="space-y-8">
          {sections.map((section, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl border border-gray-100 p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`privacy-section-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-1">
                  <section.icon className="w-5 h-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h2>
                  <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Distribuidora Santa Ana. Todos os direitos reservados.
          </p>
          <a href="/" className="text-orange-400 hover:text-orange-300 text-sm mt-2 inline-block">
            Voltar ao Catalogo
          </a>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaPrivacidade;
